// src/components/bot/aiPlanner.js
// ─────────────────────────────────────────────────────────────────────────────
// Layer 4 — AI Planner
//
// Responsibilities:
//   • Send a structured "routing" prompt to Gemini with the live endpoint catalog.
//   • Parse Gemini's JSON decision into a typed result.
//   • Return { needsApi, endpoint, params } — never throws.
//
// This is the layer that eliminates hardcoded keyword matching.
// Gemini reads the catalog and decides which endpoint (if any) is relevant.
// Adding a new endpoint to /chat-boat automatically makes it available to the AI.
// ─────────────────────────────────────────────────────────────────────────────
import { planRequest } from './geminiService';
import { getTopCandidates } from './catalogCache';

const DEBUG_AI = true;

/**
 * Ask Gemini whether the user's message requires a backend API call,
 * and if so, which endpoint from the live catalog to use.
 *
 * @param {string} userMessage - The raw user input (already trimmed)
 * @returns {Promise<{
 *   needsApi: boolean,
 *   endpoint: string | null,
 *   params: Record<string, string>
 * }>}
 */
export async function plan(userMessage, geminiHistory) {
  const catalogText = getTopCandidates(userMessage);

  if (DEBUG_AI) {
    console.debug(`[Planner] Request Started. Candidates sent: \n${catalogText}`);
  }

  // ── Call the fast planning model ─────────────────────────────────────────
  let rawResponse;
  try {
    rawResponse = await planRequest(userMessage, catalogText, geminiHistory);
    if (DEBUG_AI) {
      console.debug(`[Planner] Request Finished. Raw JSON:`, rawResponse);
    }
  } catch (e) {
    // Planning network/model failure — fall through to direct Gemini answer
    console.warn('[AiPlanner] Planning call failed:', e.message);
    return { needsApi: false, endpoint: null, params: {}, answer: '' };
  }

  // ── Parse Gemini's JSON decision ─────────────────────────────────────────
  try {
    // Strip any accidental markdown code fences (```json ... ```)
    const cleaned = rawResponse
      .replace(/```(?:json)?/gi, '')
      .replace(/```/g, '')
      .trim();

    const parsed = JSON.parse(cleaned);

    if (!parsed.needs_api) {
      if (parsed.needs_options) {
        if (DEBUG_AI) console.debug(`[Planner] needs_options: true | Location: ${parsed.location}`);
        return { needsApi: false, endpoint: null, params: {}, answer: '', needsOptions: true, location: parsed.location };
      }
      if (DEBUG_AI) console.debug(`[Planner] needs_api: false | Endpoint Selected: null`);
      return { needsApi: false, endpoint: null, params: {}, answer: parsed.answer || '' };
    }

    if (DEBUG_AI) {
      console.debug(`[Planner] needs_api: true | Endpoint Selected: ${parsed.endpoint}`);
    }

    return {
      needsApi:  true,
      endpoint:  typeof parsed.endpoint === 'string' ? parsed.endpoint.trim() : null,
      params:    parsed.params && typeof parsed.params === 'object' ? parsed.params : {},
      answer:    ''
    };
  } catch (e) {
    // Gemini returned something we couldn't parse — treat as no API needed
    console.warn('[AiPlanner] Could not parse planning JSON:', rawResponse?.slice(0, 200));
    return { needsApi: false, endpoint: null, params: {}, answer: '' };
  }
}
